import { createContext, useContext, useState, useEffect } from 'react'
import { 
  getInitialTheme, 
  saveThemePreference, 
  getThemePreference
} from '../utils/themeCache'

/**
 * ThemeContext - Gerenciamento de tema (Light/Dark Mode)
 * 
 * Funcionalidades:
 * - Alterna entre tema claro e escuro
 * - Persiste preferência no localStorage com cache robusto
 * - Respeita preferência do sistema (prefers-color-scheme)
 * - Aplica classe 'dark' no elemento raiz para ativar variáveis CSS
 * - Tratamento de erros para localStorage desabilitado ou cheio
 */

const ThemeContext = createContext()

export function ThemeProvider({ children }) {
  const [theme, setTheme] = useState(getInitialTheme)

  // Aplica o tema ao elemento raiz (html) e salva no cache
  useEffect(() => {
    const root = document.documentElement
    
    // Remove classes anteriores
    root.classList.remove('light', 'dark')
    
    // Adiciona a classe do tema atual
    root.classList.add(theme)
    
    // Salva no cache (com tratamento de erros interno)
    saveThemePreference(theme)
  }, [theme])

  // Observa mudanças na preferência do sistema (apenas se não houver preferência salva)
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
    
    const handleChange = (e) => {
      // Só atualiza se não houver preferência salva no cache
      const savedTheme = getThemePreference()
      if (!savedTheme) {
        const newTheme = e.matches ? 'dark' : 'light'
        setTheme(newTheme)
        saveThemePreference(newTheme)
      }
    }

    // Adiciona listener
    mediaQuery.addEventListener('change', handleChange)
    
    // Cleanup
    return () => {
      mediaQuery.removeEventListener('change', handleChange)
    }
  }, [])

  const toggleTheme = () => {
    setTheme(prevTheme => {
      const newTheme = prevTheme === 'light' ? 'dark' : 'light'
      // Salva imediatamente no cache
      saveThemePreference(newTheme)
      return newTheme
    })
  }

  const value = {
    theme,
    toggleTheme,
    isDark: theme === 'dark',
    isLight: theme === 'light'
  }

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  )
}

export function useTheme() {
  const context = useContext(ThemeContext)
  if (!context) {
    throw new Error('useTheme deve ser usado dentro de um ThemeProvider')
  }
  return context
}

